import { TextLink } from '@app/components/core/link/TextLink'
import { ReactNode, createElement } from 'react'

type PropertyMapItem = {
  localizationKey: string
  objectKey: string
}

export const propertyMaps = {
  initiative: [
    { objectKey: 'status', localizationKey: 'initiativeStatus' },
    { objectKey: 'users.$role', localizationKey: 'userInitiativeRole' },
  ],
}

export const pluralize = (
  translation: { singular: string; plural: string },
  number: number,
) => {
  return enrich(
    number === 1 ? translation.singular : translation.plural,
    number,
  )
}

export function resolveLocalizedField(
  value: unknown,
  language: 'nl' | 'en',
): string {
  if (typeof value === 'string') {
    return value
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const preferred = record[language]
    if (typeof preferred === 'string') {
      return preferred
    }
    if (typeof record.nl === 'string') {
      return record.nl
    }
    if (typeof record.en === 'string') {
      return record.en
    }
  }
  return ''
}

export const enrich = (translation: string, ...params: (string | number)[]) => {
  const placeholderRegex = /\{\d+\}/
  if (!placeholderRegex.test(translation) && params.length === 1) {
    return `${params[0]} ${translation}`
  }

  let processedString = translation
  params.forEach((param, index) => {
    const placeholder = `{${index}}`

    if (!translation.includes(placeholder)) {
      console.error('i18n: Param mismatch')
    }

    const paramString =
      param !== undefined && param !== null ? param.toString() : ''
    processedString = processedString.replace(placeholder, paramString)
  })

  if (placeholderRegex.test(processedString)) {
    console.error('i18n: Param mismatch')
  }

  return processedString
}

/**
 * Convert translation strings containing simple `[label](https://example.com)` tags into a ReactNode.
 *
 * Example:
 * `enrichWithLinks('Hello [world](https://example.com)!')`
 */
export const enrichWithLinks = (translation: string): ReactNode => {
  const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g

  const parseToNodes = (input: string, regex: RegExp): ReactNode[] => {
    const nodes: ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null
    let linkIndex = 0

    while ((match = regex.exec(input)) !== null) {
      const full = match[0]
      const start = match.index
      const end = start + full.length

      if (start > lastIndex) {
        nodes.push(input.slice(lastIndex, start))
      }

      const text = match[1] ?? ''
      const href = match[2] ?? ''

      nodes.push(
        createElement(TextLink, {
          key: `link-${linkIndex++}`,
          to: href,
          target: '_blank',
          children: text,
        }),
      )
      lastIndex = end
    }

    if (lastIndex < input.length) {
      nodes.push(input.slice(lastIndex))
    }

    return nodes
  }

  if (!mdLinkRegex.test(translation)) {
    return translation
  }

  mdLinkRegex.lastIndex = 0
  return parseToNodes(translation, mdLinkRegex)
}

export const localize = (
  localizationSource: any,
  entity: any,
  propertyMap: PropertyMapItem[],
): any[] => {
  const localization = Array.isArray(localizationSource)
    ? localizationSource[0]
    : localizationSource

  const collection = Array.isArray(entity) ? entity : [entity]

  return collection.map((object) => {
    propertyMap.forEach((p) => {
      if (p.objectKey.includes('.')) {
        const parts = p.objectKey.split('.')
        const target = object[parts[0]]
        if (target === undefined) {
          throw new Error(
            `Complex property ${
              p.objectKey
            } missing list in object ${JSON.stringify(object)} at property ${
              parts[0]
            }`,
          )
        }

        const nestedPropertyMap = [
          {
            localizationKey: p.localizationKey,
            objectKey: parts.slice(1).join('.'),
          },
        ]
        localize(localization, target, nestedPropertyMap)
        return
      }

      const preLocalized = object[p.objectKey]
      if (object[p.objectKey] === undefined) {
        throw new Error(
          `Property ${p.objectKey} is missing on the object ${JSON.stringify(
            object,
          )}`,
        )
      }
      if (localization[p.localizationKey] === undefined) {
        throw new Error(
          `Property ${
            p.localizationKey
          } is missing on the object ${JSON.stringify(localization)}`,
        )
      }
      object[p.objectKey] = localization[p.localizationKey][preLocalized]
    })
    return object
  })
}
